import { Button, Container } from '@playcanvas/pcui';

import { NumericInput } from '../ui/numeric-input';
import { TranslateGizmo, Vec3 } from 'playcanvas';

import { Events } from '../events';
import { Scene } from '../scene';
import { SphereShape } from '../sphere-shape';
import { Splat } from '../splat';
import { localize } from '../ui/localization';

class SphereSelection {
    activate: () => void;
    deactivate: () => void;

    active = false;

    constructor(events: Events, scene: Scene, canvasContainer: Container) {
        const sphere = new SphereShape();

        const gizmo = new TranslateGizmo(scene.camera.camera, scene.gizmoLayer);

        gizmo.on('render:update', () => {
            scene.forceRender = true;
        });

        gizmo.on('transform:move', () => {
            sphere.moved();
        });

        // ui
        const selectToolbar = new Container({
            class: 'select-toolbar',
            hidden: true
        });

        selectToolbar.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
        });

        const selectButton = new Button({ text: localize('toolbar.select'), class: 'select-toolbar-button' });
        const invertButton = new Button({ text: localize('toolbar.invert'), class: 'select-toolbar-button' });
        const deleteButton = new Button({
            text: localize('menu.select.delete'),
            // PCUI passes `class` to classList.add (one token only).
            class: 'select-toolbar-button--danger'
        });
        const radius = new NumericInput({
            precision: 2,
            value: sphere.radius,
            width: 80,
            min: 0.01
        });

        const radiusWrap = document.createElement('span');
        radiusWrap.className = 'select-toolbar-numeric-wrap';
        selectToolbar.append(selectButton);
        selectToolbar.append(invertButton);
        selectToolbar.append(deleteButton);
        selectToolbar.dom.appendChild(radiusWrap);
        radiusWrap.appendChild(radius.dom);
        const radiusSuffix = document.createElement('span');
        radiusSuffix.className = 'select-toolbar-unit';
        radiusSuffix.textContent = localize('toolbar.radius');
        radiusWrap.appendChild(radiusSuffix);

        canvasContainer.append(selectToolbar);

        const applySphere = (op: 'add') => {
            const p = sphere.pivot.getPosition();
            events.fire('select.bySphere', op, [p.x, p.y, p.z, sphere.radius]);
        };

        selectButton.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            applySphere('add');
        });
        invertButton.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            events.fire('select.invert');
        });
        deleteButton.dom.addEventListener('pointerdown', (e) => {
            e.stopPropagation();
            events.fire('select.delete');
        });
        radius.on('change', () => {
            sphere.radius = radius.value;
        });

        events.on('camera.focalPointPicked', (details: { splat: Splat, position: Vec3 }) => {
            if (this.active) {
                sphere.pivot.setPosition(details.position);
                gizmo.attach([sphere.pivot]);
            }
        });

        const updateGizmoSize = () => {
            const { camera, canvas } = scene;
            if (camera.ortho) {
                gizmo.size = 1125 / canvas.clientHeight;
            } else {
                gizmo.size = 1200 / Math.max(canvas.clientWidth, canvas.clientHeight);
            }
        };
        updateGizmoSize();
        events.on('camera.resize', updateGizmoSize);
        events.on('camera.ortho', updateGizmoSize);

        this.activate = () => {
            this.active = true;
            scene.add(sphere);
            gizmo.attach([sphere.pivot]);
            selectToolbar.hidden = false;
        };

        this.deactivate = () => {
            selectToolbar.hidden = true;
            gizmo.detach();
            scene.remove(sphere);
            this.active = false;
        };
    }
}

export { SphereSelection };
